import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { createMap, memberIcon, MEMBER_COLORS, fmtAgo, haversine, fmtDuration } from "./mapUtils";
import { CrosshairSimple, BatteryMedium, Path, CaretLeft, CaretRight, X, GearSix, FlagBanner, Check, MagnifyingGlass, PersonSimpleWalk, Bicycle, Car, BookmarkSimple, ClockCounterClockwise } from "@phosphor-icons/react";
import LocationPrivacyDialog from "./LocationPrivacyDialog";
import GeofenceLog from "./GeofenceLog";
import { toast } from "sonner";

const dayIso = (d) => d.toISOString().slice(0, 10);
const shiftDay = (iso, delta) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + delta);
  return dayIso(d);
};

const ZONE_ICONS = { home: "🏠", school: "🎓", work: "💼", other: "📍" };
const ZONE_PRESETS = [["home", "🏠", "Дом"], ["school", "🎓", "Школа"], ["work", "💼", "Работа"], ["other", "📍", "Другое"]];

export default function FamilyMap() {
  const { user } = useAuth();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const circlesRef = useRef({});
  const fittedRef = useRef(false);
  const pathLayerRef = useRef(null);
  const [members, setMembers] = useState([]);
  const [hist, setHist] = useState(null);
  const [histData, setHistData] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [navTarget, setNavTarget] = useState(null);
  const [navName, setNavName] = useState("");
  const [pickMode, setPickMode] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState(null);
  const [navMode, setNavMode] = useState("foot");
  const [savedPoints, setSavedPoints] = useState([]);
  const [zones, setZones] = useState([]);
  const [zonePick, setZonePick] = useState(null);
  const [showZoneLog, setShowZoneLog] = useState(false);
  const pickRef = useRef(false);
  const zonePickRef = useRef(null);
  const zoneLayersRef = useRef({});
  const navMarkerRef = useRef(null);
  const navLineRef = useRef(null);
  pickRef.current = pickMode;
  zonePickRef.current = zonePick;

  const drawNavLine = (t) => {
    navLineRef.current?.remove();
    navLineRef.current = null;
    if (!t || !mapRef.current || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => {
      if (!mapRef.current) return;
      const from = [p.coords.latitude, p.coords.longitude];
      const km = mapRef.current.distance(from, [t.lat, t.lng]) / 1000;
      navLineRef.current = L.polyline([from, [t.lat, t.lng]],
        { color: "#c792ea", weight: 3, dashArray: "8 8", opacity: 0.85 })
        .bindTooltip(`${km < 1 ? Math.round(km * 1000) + " м" : km.toFixed(1) + " км"} по прямой`, { permanent: true, direction: "center", className: "nav-dist-tip" })
        .addTo(mapRef.current);
    }, () => {}, { maximumAge: 30000, timeout: 8000 });
  };

  const drawNavMarker = (t) => {
    navMarkerRef.current?.remove();
    navMarkerRef.current = null;
    if (!t || !mapRef.current) return;
    navMarkerRef.current = L.marker([t.lat, t.lng], {
      icon: L.divIcon({ className: "", html: '<div style="font-size:26px;line-height:1;filter:drop-shadow(0 1px 3px rgba(0,0,0,.7))">🚩</div>', iconAnchor: [4, 26] }),
    }).addTo(mapRef.current).bindPopup(`Точка для очков: <b>${t.name}</b>`);
    drawNavLine(t);
  };

  useEffect(() => {
    api.get("/nav/target").then((r) => {
      setNavTarget(r.data.target);
      setNavName(r.data.target?.name || "");
      setNavMode(r.data.target?.mode || "foot");
      drawNavMarker(r.data.target);
    }).catch(() => {});
    api.get("/nav/points").then((r) => setSavedPoints(r.data.points)).catch(() => {});
    api.get("/geofences").then((r) => setZones(r.data.zones)).catch(() => {});
  }, []);

  const createZone = async (zp, lat, lng) => {
    try {
      const { data } = await api.post("/geofences", { name: zp.name || "Зона", icon: zp.icon, radius_m: zp.radius, lat, lng });
      setZones((z) => [...z, data.zone]);
      mapRef.current?.flyTo([lat, lng], 15, { duration: 0.8 });
      toast.success(`Зона «${data.zone.name}» создана — семья получит push при входе и выходе`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Не удалось создать зону"); }
  };

  const deleteZone = async (z) => {
    try {
      await api.delete(`/geofences/${z.id}`);
      setZones((list) => list.filter((x) => x.id !== z.id));
      toast.success(`Зона «${z.name}» удалена`);
    } catch { toast.error("Не удалось удалить зону"); }
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    Object.values(zoneLayersRef.current).forEach((g) => g.remove());
    zoneLayersRef.current = {};
    zones.forEach((z) => {
      const g = L.layerGroup().addTo(map);
      L.circle([z.lat, z.lng], { radius: z.radius_m, color: "#4ade80", weight: 1.5, opacity: 0.75,
        fillColor: "#4ade80", fillOpacity: 0.09, dashArray: "6 6" }).addTo(g);
      L.marker([z.lat, z.lng], { icon: L.divIcon({ className: "", iconAnchor: [13, 13],
        html: `<div style="font-size:22px;line-height:1;filter:drop-shadow(0 1px 3px rgba(0,0,0,.7))">${ZONE_ICONS[z.icon] || "📍"}</div>` }) })
        .bindPopup(`<b>${ZONE_ICONS[z.icon] || "📍"} ${z.name}</b><br/>радиус ${z.radius_m} м · push семье при входе и выходе`).addTo(g);
      zoneLayersRef.current[z.id] = g;
    });
  }, [zones]);

  const setMode = async (m) => {
    setNavMode(m);
    try { await api.post("/nav/mode", { mode: m }); } catch {}
  };

  const savePointToList = async () => {
    if (!navTarget) return;
    try {
      const { data } = await api.post("/nav/points", { lat: navTarget.lat, lng: navTarget.lng, name: navName || "Точка" });
      setSavedPoints((p) => [data.point, ...p.filter((x) => x.id !== data.point.id && x.name !== data.point.name)]);
      toast.success(`«${data.point.name}» в сохранённых`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Не удалось сохранить"); }
  };

  const activatePoint = async (p) => {
    try {
      const { data } = await api.post(`/nav/points/${p.id}/activate`);
      setNavTarget(data.target);
      setNavName(data.target.name);
      drawNavMarker(data.target);
      mapRef.current?.flyTo([data.target.lat, data.target.lng], 15, { duration: 1 });
      toast.success(`Очки ведут к «${data.target.name}»`);
    } catch { toast.error("Не удалось активировать точку"); }
  };

  const deletePoint = async (p) => {
    try {
      await api.delete(`/nav/points/${p.id}`);
      setSavedPoints((l) => l.filter((x) => x.id !== p.id));
    } catch {}
  };

  const saveTarget = async (lat, lng, name) => {
    try {
      const { data } = await api.post("/nav/target", { lat, lng, name: name || "Точка" });
      setNavTarget(data.target);
      setNavName(data.target.name);
      drawNavMarker(data.target);
      toast.success("Точка сохранена — стрелка на очках уже ведёт к ней");
    } catch { toast.error("Не удалось сохранить точку"); }
  };

  const clearTarget = async () => {
    try {
      await api.delete("/nav/target");
      setNavTarget(null);
      setNavName("");
      drawNavMarker(null);
      navLineRef.current?.remove();
      navLineRef.current = null;
    } catch {}
  };

  const searchAddress = async () => {
    if (searchQ.trim().length < 3) return;
    try {
      const { data } = await api.get(`/nav/search?q=${encodeURIComponent(searchQ)}`);
      setSearchRes(data.results);
      if (!data.results.length) toast.info("Ничего не найдено — уточните адрес");
    } catch { toast.error("Поиск недоступен"); }
  };

  const pickSearchResult = (r) => {
    setSearchRes(null);
    setSearchQ("");
    saveTarget(r.lat, r.lng, r.name);
    mapRef.current?.flyTo([r.lat, r.lng], 15, { duration: 1.2 });
  };

  useEffect(() => {
    mapRef.current = createMap(mapEl.current);
    mapRef.current.on("click", (e) => {
      if (zonePickRef.current) {
        const zp = zonePickRef.current;
        setZonePick(null);
        createZone(zp, e.latlng.lat, e.latlng.lng);
        return;
      }
      if (!pickRef.current) return;
      setPickMode(false);
      saveTarget(e.latlng.lat, e.latlng.lng, navName || "Точка");
    });
    return () => mapRef.current?.remove();
  }, []);

  useEffect(() => {
    const load = () => api.get("/family/locations").then((r) => setMembers(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [refreshKey]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    members.forEach((m, i) => {
      const color = MEMBER_COLORS[i % MEMBER_COLORS.length];
      const pos = [m.lat, m.lng];
      if (markersRef.current[m.id]) {
        markersRef.current[m.id].setLatLng(pos);
      } else {
        markersRef.current[m.id] = L.marker(pos, { icon: memberIcon(m.name, color) }).addTo(map);
      }
      if (m.accuracy != null && m.accuracy > 5) {
        if (circlesRef.current[m.id]) {
          circlesRef.current[m.id].setLatLng(pos).setRadius(m.accuracy);
        } else {
          circlesRef.current[m.id] = L.circle(pos, { radius: m.accuracy, color, weight: 1, opacity: 0.4, fillOpacity: 0.08 }).addTo(map);
        }
      }
      markersRef.current[m.id].bindPopup(
        `<b>${m.name}</b><br/>${fmtAgo(m.updated_at)}${m.accuracy != null ? ` · точность ±${Math.round(m.accuracy)} м` : ""}${m.battery != null ? ` · 🔋 ${m.battery}%` : ""}`);
    });
    if (!fittedRef.current && members.length > 0) {
      fittedRef.current = true;
      map.fitBounds(L.latLngBounds(members.map((m) => [m.lat, m.lng])).pad(0.3), { maxZoom: 15 });
    }
  }, [members]);

  const focusOn = (m) => mapRef.current?.setView([m.lat, m.lng], 15);

  useEffect(() => {
    const map = mapRef.current;
    if (pathLayerRef.current) { pathLayerRef.current.remove(); pathLayerRef.current = null; }
    setHistData(null);
    if (!hist || !map) return;
    const from = new Date(`${hist.date}T00:00:00`);
    const to = new Date(from.getTime() + 86400000);
    api.get(`/family/locations/history?member=${hist.id}&from=${from.toISOString()}&to=${to.toISOString()}`)
      .then((r) => {
        setHistData(r.data);
        const pts = r.data.points;
        if (!pts.length) return;
        const g = L.layerGroup().addTo(map);
        L.polyline(pts.map((p) => [p.lat, p.lng]), { color: hist.color, weight: 4, opacity: 0.85 }).addTo(g);
        const dot = (p, label) => L.circleMarker([p.lat, p.lng], { radius: 6, color: "#fff", weight: 2, fillColor: hist.color, fillOpacity: 1 })
          .bindPopup(`${label} ${new Date(p.t).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`).addTo(g);
        dot(pts[0], "Старт");
        if (pts.length > 1) dot(pts[pts.length - 1], "Финиш");
        pathLayerRef.current = g;
        map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])).pad(0.2), { maxZoom: 16 });
      })
      .catch((err) => setHistData({ points: [], distance_m: 0,
        denied: err?.response?.status === 403 }));
  }, [hist]);

  const showPath = (m, color) => setHist({ id: m.id, name: m.name, color, date: dayIso(new Date()) });

  return (
    <div className="space-y-3" data-testid="family-map">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="nav-target-panel">
          <button onClick={() => setPickMode((p) => !p)} data-testid="nav-pick-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${pickMode ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            <FlagBanner size={14} weight={navTarget ? "fill" : "regular"} />
            {pickMode ? "Кликните по карте…" : navTarget ? "Переставить точку" : "Точка для очков"}
          </button>
          {navTarget && !pickMode && (
            <>
              <input value={navName} onChange={(e) => setNavName(e.target.value)} data-testid="nav-name-input"
                placeholder="Название точки"
                className="bg-secondary rounded-md px-2.5 py-1.5 text-xs w-36 outline-none focus:ring-1 focus:ring-primary" />
              <button onClick={() => saveTarget(navTarget.lat, navTarget.lng, navName)} data-testid="nav-name-save"
                className="p-1.5 rounded-md bg-secondary text-muted-foreground hover:text-primary transition-colors"><Check size={14} /></button>
              <button onClick={clearTarget} data-testid="nav-clear-btn"
                className="p-1.5 rounded-md bg-secondary text-muted-foreground hover:text-red-400 transition-colors"><X size={14} /></button>
            </>
          )}
          <div className="relative">
            <div className="flex items-center gap-1">
              <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchAddress()} data-testid="nav-search-input"
                placeholder="Адрес или место…"
                className="bg-secondary rounded-md px-2.5 py-1.5 text-xs w-44 outline-none focus:ring-1 focus:ring-primary" />
              <button onClick={searchAddress} data-testid="nav-search-btn"
                className="p-1.5 rounded-md bg-secondary text-muted-foreground hover:text-primary transition-colors"><MagnifyingGlass size={14} /></button>
            </div>
            {searchRes && searchRes.length > 0 && (
              <div className="absolute top-full left-0 mt-1 z-[1000] glass-card p-1 w-72 max-w-[80vw]" data-testid="nav-search-results">
                {searchRes.map((r, i) => (
                  <button key={i} onClick={() => pickSearchResult(r)} data-testid={`nav-search-result-${i}`}
                    className="block w-full text-left px-2.5 py-2 rounded-md hover:bg-white/10 transition-colors">
                    <div className="text-xs font-semibold truncate">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{r.display}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={() => setShowPrivacy(true)} data-testid="privacy-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-secondary text-muted-foreground hover:text-foreground transition-colors">
          <GearSix size={14} /> Приватность
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="nav-mode-row">
        <div className="flex rounded-md overflow-hidden border border-white/10">
          {[["foot", PersonSimpleWalk, "Пешком"], ["bike", Bicycle, "Вело"], ["car", Car, "Авто"]].map(([m, Icon, label]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`nav-mode-${m}`}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold transition-colors ${navMode === m ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <Icon size={14} weight={navMode === m ? "fill" : "regular"} />{label}
            </button>
          ))}
        </div>
        {navTarget && (
          <button onClick={savePointToList} data-testid="nav-save-point-btn"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-secondary text-muted-foreground hover:text-primary transition-colors">
            <BookmarkSimple size={14} />В сохранённые
          </button>
        )}
        {savedPoints.map((p) => (
          <span key={p.id} className={`flex items-center gap-1 rounded-full pl-2.5 pr-1 py-1 text-xs font-semibold transition-colors ${navTarget && navTarget.name === p.name ? "bg-primary/25 text-primary ring-1 ring-primary/40" : "bg-secondary text-muted-foreground"}`}
            data-testid={`nav-point-chip-${p.name}`}>
            <button onClick={() => activatePoint(p)} className="hover:text-primary transition-colors">{p.name}</button>
            <button onClick={() => deletePoint(p)} className="p-0.5 hover:text-red-400 transition-colors"><X size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap" data-testid="geofence-row">
        <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Зоны</span>
        {zones.map((z) => (
          <span key={z.id} data-testid={`geofence-chip-${z.name}`}
            className="flex items-center gap-1 rounded-full pl-2.5 pr-1 py-1 text-xs font-semibold bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/25">
            <button onClick={() => mapRef.current?.flyTo([z.lat, z.lng], 15, { duration: 0.8 })} className="hover:text-emerald-100 transition-colors">
              {ZONE_ICONS[z.icon] || "📍"} {z.name}
            </button>
            <button onClick={() => deleteZone(z)} data-testid={`geofence-delete-${z.name}`} className="p-0.5 hover:text-red-400 transition-colors"><X size={11} /></button>
          </span>
        ))}
        {!zonePick ? ZONE_PRESETS.map(([icon, emoji, label]) => (
          <button key={icon} onClick={() => setZonePick({ icon, name: icon === "other" ? "" : label, radius: 200 })} data-testid={`geofence-add-${icon}`}
            className="px-2.5 py-1 rounded-full text-xs font-semibold bg-secondary text-muted-foreground hover:text-foreground border border-dashed border-white/15 transition-colors">
            + {emoji} {label}
          </button>
        )) : (
          <span className="flex items-center gap-1.5 text-xs font-semibold" data-testid="geofence-pick-panel">
            <input value={zonePick.name} onChange={(e) => setZonePick({ ...zonePick, name: e.target.value })} data-testid="geofence-name-input"
              placeholder="Название зоны" autoFocus
              className="bg-secondary rounded-md px-2 py-1 w-28 text-foreground outline-none focus:ring-1 focus:ring-primary" />
            <select value={zonePick.radius} onChange={(e) => setZonePick({ ...zonePick, radius: Number(e.target.value) })} data-testid="geofence-radius-select"
              className="bg-secondary rounded-md px-1.5 py-1 text-foreground outline-none">
              {[100, 200, 300, 500, 1000].map((r) => <option key={r} value={r}>{r} м</option>)}
            </select>
            <span className="text-primary animate-pulse">Кликните по карте…</span>
            <button onClick={() => setZonePick(null)} data-testid="geofence-pick-cancel" className="p-1 rounded hover:bg-secondary transition-colors"><X size={12} /></button>
          </span>
        )}
        {zones.length > 0 && (
          <button onClick={() => setShowZoneLog((s) => !s)} data-testid="geofence-log-btn"
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${showZoneLog ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            <ClockCounterClockwise size={13} /> Лента
          </button>
        )}
      </div>
      {showZoneLog && <GeofenceLog onClose={() => setShowZoneLog(false)} />}
      {hist && (
        <div className="glass-card p-3 flex items-center gap-2 flex-wrap" data-testid="history-panel">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: hist.color }} />
          <span className="text-sm font-semibold">{hist.name}</span>
          <div className="flex items-center gap-1 ml-1">
            <button onClick={() => setHist({ ...hist, date: shiftDay(hist.date, -1) })} data-testid="hist-prev-day"
              className="p-1 rounded hover:bg-secondary transition-colors"><CaretLeft size={14} /></button>
            <span className="text-xs num">{new Date(hist.date + "T12:00:00").toLocaleDateString("ru-RU", { weekday: "short", day: "numeric", month: "short" })}</span>
            <button onClick={() => setHist({ ...hist, date: shiftDay(hist.date, 1) })} disabled={hist.date >= dayIso(new Date())} data-testid="hist-next-day"
              className="p-1 rounded hover:bg-secondary transition-colors disabled:opacity-30"><CaretRight size={14} /></button>
          </div>
          <span className="text-xs text-muted-foreground num flex-1" data-testid="hist-stats">
            {histData == null ? "Загружаем…" : histData.denied ? "Доступ к передвижениям ограничен"
              : histData.points.length === 0 ? "Нет точек за этот день"
              : `${(histData.distance_m / 1000).toFixed(2)} км · ${histData.points.length} точек`}
          </span>
          <button onClick={() => setHist(null)} data-testid="hist-close" className="p-1 rounded hover:bg-secondary transition-colors"><X size={15} /></button>
        </div>
      )}
      <div ref={mapEl} className="h-[45vh] sm:h-[55vh] rounded-xl overflow-hidden border border-white/[0.08]" data-testid="family-map-canvas" />
      {members.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Пока нет отметок. Позиция появляется автоматически, когда член семьи открывает LifeOS и разрешает геолокацию.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="family-locations-list">
          {members.map((m, i) => (
            <button key={m.id} onClick={() => focusOn(m)} data-testid={`loc-member-${m.id}`}
              className="glass-card p-3 flex items-center gap-3 text-left">
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-extrabold shrink-0"
                style={{ background: MEMBER_COLORS[i % MEMBER_COLORS.length] }}>{m.name[0]}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{m.name}{m.id === user?.id ? " (вы)" : ""}</div>
                <div className="text-[11px] text-muted-foreground">{fmtAgo(m.updated_at)}</div>
              </div>
              {m.battery != null && (
                <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground num shrink-0">
                  <BatteryMedium size={14} /> {m.battery}%
                </span>
              )}
              <span onClick={(e) => { e.stopPropagation(); showPath(m, MEMBER_COLORS[i % MEMBER_COLORS.length]); }}
                data-testid={`loc-history-${m.id}`} title="Путь за день"
                className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-secondary transition-colors shrink-0">
                <Path size={16} />
              </span>
              <CrosshairSimple size={15} className="text-primary shrink-0" />
            </button>
          ))}
        </div>
      )}
      <LocationPrivacyDialog open={showPrivacy} meId={user?.id}
        onClose={(saved) => {
          setShowPrivacy(false);
          if (saved) {
            Object.values(markersRef.current).forEach((mk) => mk.remove());
            Object.values(circlesRef.current).forEach((c) => c.remove());
            markersRef.current = {};
            circlesRef.current = {};
            setRefreshKey((k) => k + 1);
          }
        }} />
    </div>
  );
}
